// Passerelle locale: l'adaptateur axios par lequel passent toutes les requêtes.
//
//  - Lecture « local d'abord »: une adresse déjà lue ici est montrée tout de
//    suite depuis les données locales, puis revalidée en silence auprès du
//    serveur; si le contenu a changé, les écrans abonnés se rechargent sans état
//    de chargement (lib/fraicheur.js). Si la dernière confirmation du serveur est
//    trop ancienne, la réponse du serveur est préférée quand elle arrive vite.
//    Une adresse jamais lue attend le serveur; sans réseau, la réponse est
//    reconstruite localement.
//  - Écriture connue de `writeRoutes.js`: tentée en ligne avec une clé de rejeu;
//    si le réseau manque, elle est appliquée localement puis mise en file, et
//    l'écran reçoit tout de suite une réponse de succès.
//  - Tout le reste (connexion, encaissements, émission de facture…) passe
//    tel quel et échoue comme avant sans réseau.
import axios, { AxiosError } from 'axios';
import { ALIAS_DE_LECTURE } from './kinds';
import { cleDeLecture, decouperAdresse, copie, nouvelIdentifiant } from './paths';
import { enregistrerLecture, lireHorsLigne, lireLocalRapide } from './readModel';
import {
  ecrireEntiteLocale,
  ecrireEntitesServeur,
  ecrireFichier,
  instantDerniereEcriture,
  instantDernierChangement,
  lireEntite,
  lireFichier,
  noterEcriture,
  nombreEcrituresAEnvoyer,
  retirerEntiteLocale,
} from './store';
import { DEPENDANCES, signalerDonneesModifiees } from '../fraicheur';
import { trouverRouteEcriture } from './writeRoutes';
import { RefusLocal, refusLocal } from './droits';
import { debutPremierPlan, finPremierPlan } from './arrierePlan';
import './derived/resolveurs';

export const TYPE_REQUETE_EN_FILE = 'http';

const EXCLUES = [
  /^\/auth(\/|$)/,
  /^\/users\/login/,
  /^\/sync(\/|$)/,
  /^\/integrations(\/|$)/,
  /^\/backend-ops(\/|$)/,
  /^\/tracking\/batch/,
];
const DELAI_PREMIERE_TENTATIVE = 15000;

// Local d'abord: au-delà de cet âge, le serveur est préféré s'il répond vite.
export const AGE_MAX_LOCAL = 2 * 60 * 1000;
export const BUDGET_RESEAU = 1500;
// Une même adresse n'est pas revalidée plus souvent que cela (le signal d'un changement, lui, au plus toutes les 30 s).
export const DELAI_REVALIDATION = 3000;
export const DELAI_SIGNAL = 30000;
// Données vivantes (positions, notifications): toujours le serveur d'abord; la
// dernière valeur connue ne sert qu'en secours.
const SERVEUR_D_ABORD = [
  /^\/tracking(\/|$)/,
  /^\/notifications(\/|$)/,
  /^\/alerts\/events\/summary$/, // compteur interrogé en boucle par la barre du haut
  /^\/(admin|licenses|organizations|audit|demo)(\/|$)|^\/users(?!\/me(\/|$))(\/|$)/, // plateforme et comptes: jamais d'instantané
];
const ENTETES_CONSERVES = ['Idempotency-Key', 'X-Org-Id'];

const concerne = (chemin) => !EXCLUES.some((motif) => motif.test(chemin));

const estBinaire = (config) => ['blob', 'arraybuffer', 'stream'].includes(config?.responseType);

const erreurReseau = (config) => new AxiosError('Network Error', AxiosError.ERR_NETWORK, config);

const lireCorps = (config) => {
  const brut = config?.data;
  if (brut === undefined || brut === null || brut === '') return null;
  if (typeof FormData !== 'undefined' && brut instanceof FormData) return undefined;
  if (typeof brut === 'string') {
    try {
      return JSON.parse(brut);
    } catch (erreur) {
      return undefined;
    }
  }
  return typeof brut === 'object' ? copie(brut) : undefined;
};

const lireEntetes = (config, noms) => {
  const resultat = {};
  const source = config?.headers;
  noms.forEach((nom) => {
    const valeur = typeof source?.get === 'function' ? source.get(nom) : source?.[nom];
    if (valeur) resultat[nom] = String(valeur);
  });
  return resultat;
};

// À la sortie de l'adaptateur, le corps n'est pas encore décodé (axios le fait
// ensuite): on lit le JSON sans toucher à la réponse rendue à l'écran.
const contenuJson = (reponse) => {
  const brut = reponse?.data;
  if (typeof brut !== 'string') return brut;
  try {
    return JSON.parse(brut);
  } catch (erreur) {
    return undefined;
  }
};

// Refus de droits: la même erreur que celle du serveur, pour les mêmes écrans.
const refusDeDroits = (config, message, statut = 403, code = 'AUTHORIZATION_ERROR') => {
  const reponse = {
    data: { success: false, error: { code, message } },
    status: statut,
    statusText: statut === 403 ? 'Forbidden' : 'Unprocessable Entity',
    headers: {},
    config,
    request: {},
  };
  return new AxiosError(message, AxiosError.ERR_BAD_REQUEST, config, {}, reponse);
};

const reponseLocale = (config, data, { statut = 200, entetes = {} } = {}) => ({
  data,
  status: statut,
  statusText: 'OK',
  headers: entetes,
  config,
  request: {},
});

export function installerPasserelle(instance, hooks) {
  // Un client axios simulé (tests) n'a ni réglages ni adaptateur: rien à brancher.
  if (!instance?.defaults || typeof axios.getAdapter !== 'function') {
    return { attendre: () => Promise.resolve() };
  }
  const adaptateurDeBase = axios.getAdapter(instance.defaults.adapter || axios.defaults.adapter);
  // Les mémorisations s'enchaînent dans l'ordre; une lecture hors ligne attend
  // la dernière en cours pour ne pas lire un état plus ancien que l'écran.
  let enCours = Promise.resolve();
  const enfiler = (travail) => {
    enCours = enCours.then(travail).catch((erreur) => {
      console.warn('Local read model update failed:', erreur);
    });
    return enCours;
  };

  const memoriser = (chemin, parametres, reponse, demandeeLe = null) => {
    if (estBinaire(reponse.config)) return;
    const contenu = contenuJson(reponse);
    if (contenu === undefined || contenu === null || typeof contenu !== 'object') return;
    const instantane = copie(contenu);
    const entetesCompte = {};
    ['x-total-count', 'x-count-propres', 'x-count-loues'].forEach((nom) => {
      const valeur = reponse.headers?.[nom];
      if (valeur !== undefined && valeur !== null) entetesCompte[nom] = String(valeur);
    });
    enfiler(() =>
      enregistrerLecture({
        chemin,
        parametres,
        data: instantane,
        status: reponse.status,
        headers: entetesCompte,
        demandeeLe,
      }),
    );
  };

  // --- Local d'abord --------------------------------------------------------------------
  const revalidations = new Map(); // adresse -> requête en cours
  const enVol = new Set(); // réponses tardives attendues en arrière-plan
  const dernierSignal = new Map(); // adresse -> instant

  // Deux contenus sont « les mêmes » quand seuls des champs volatils (heure de
  // calcul, identifiant de requête…) diffèrent: sinon chaque revalidation
  // signalerait un changement qui n'en est pas un.
  const VOLATILS = /(^|_)(generated|computed|fetched|refreshed|requested|timestamp|request|elapsed|duration)(_|$)|^server_time$|^as_of$|^now$/i;
  const normaliser = (valeur) => {
    if (Array.isArray(valeur)) return valeur.map(normaliser);
    if (valeur && typeof valeur === 'object') {
      return Object.keys(valeur)
        .filter((cle) => !VOLATILS.test(cle))
        .sort()
        .reduce((acc, cle) => ({ ...acc, [cle]: normaliser(valeur[cle]) }), {});
    }
    return valeur;
  };
  const memeContenu = (a, b) => {
    try {
      return JSON.stringify(normaliser(a)) === JSON.stringify(normaliser(b));
    } catch (erreur) {
      return false;
    }
  };

  const signalerChangement = (chemin, cle) => {
    const maintenant = Date.now();
    if (maintenant - (dernierSignal.get(cle) || 0) < DELAI_SIGNAL) return;
    dernierSignal.set(cle, maintenant);
    const ressource = chemin.split('/')[1] || '';
    const domaines = DEPENDANCES[ressource];
    if (domaines?.length) signalerDonneesModifiees(domaines);
  };

  // Requête serveur d'une lecture: sa réponse est gardée sur l'appareil.
  const interroger = async (config, chemin, parametres) => {
    const demandeeLe = Date.now();
    const reponse = await adaptateurDeBase(config);
    memoriser(chemin, parametres, reponse, demandeeLe);
    return reponse;
  };

  // Revalidation silencieuse: jamais de message, jamais de chargement. Si le
  // serveur dit autre chose que ce qui a été montré, les écrans abonnés se rechargent.
  const revalider = (config, chemin, parametres, montre, enAttente) => {
    const cle = cleDeLecture(chemin, parametres);
    if (revalidations.has(cle)) return revalidations.get(cle);
    const travail = (async () => {
      try {
        const copieConfig = { ...config, timeout: Math.min(config.timeout || 15000, 15000) };
        const reponse = await interroger(copieConfig, chemin, parametres);
        await enCours;
        // Des écritures encore à envoyer rendent la comparaison sans objet:
        // le rejeu signalera lui-même les écrans (intercepteur de réponse).
        if (enAttente || (await nombreEcrituresAEnvoyer()) > 0) return;
        if (!memeContenu(contenuJson(reponse), montre)) signalerChangement(chemin, cle);
      } catch (erreur) {
        if (!erreur?.response && erreur?.code !== 'ERR_CANCELED') hooks.sonder?.();
      } finally {
        revalidations.delete(cle);
      }
    })();
    revalidations.set(cle, travail);
    return travail;
  };

  const attendre = (ms) => new Promise((resolve) => setTimeout(() => resolve(null), ms));

  // Fichiers (PDF, rapports, photos): la dernière version consultée est gardée.
  async function lireFichierBinaire(config, chemin, parametres) {
    const cle = cleDeLecture(chemin, parametres);
    if (hooks.enLigne()) {
      try {
        const reponse = await adaptateurDeBase(config);
        if (reponse?.data && typeof reponse.data === 'object' && typeof reponse.data.size === 'number') {
          const blob = reponse.data;
          enfiler(() => ecrireFichier(cle, blob));
        }
        return reponse;
      } catch (erreur) {
        if (erreur?.response || erreur?.code === 'ERR_CANCELED') throw erreur;
        hooks.sonder?.();
      }
    }
    await enCours;
    const blob = await lireFichier(cle);
    if (blob) return reponseLocale(config, blob, { entetes: { 'content-type': blob.type || '' } });
    throw erreurReseau(config);
  }

  async function lire(config) {
    const { chemin, parametres } = decouperAdresse(config);
    if (!concerne(chemin)) return adaptateurDeBase(config);
    if (estBinaire(config)) {
      return config.responseType === 'stream' ? adaptateurDeBase(config) : lireFichierBinaire(config, chemin, parametres);
    }

    if (hooks.enLigne()) {
      const localPret = hooks.localPret ? hooks.localPret() : true;
      const serveurDabord = config.reseauDabord || !localPret || SERVEUR_D_ABORD.some((motif) => motif.test(chemin));
      let locale = null;
      let enAttente = false;
      if (!serveurDabord) {
        await enCours;
        enAttente = (await nombreEcrituresAEnvoyer()) > 0;
        try {
          locale = await lireLocalRapide({ chemin, parametres, enAttente });
        } catch (erreur) {
          locale = null;
        }
      }

      if (locale) {
        const maintenant = Date.now();
        const confirmeLe = locale.ts || 0;
        // Frais: confirmé récemment par le serveur, sans écriture depuis ni mise à
        // jour plus récente que cet instantané.
        // Des écritures encore à envoyer: seul le local les contient, il passe avant.
        const frais =
          enAttente ||
          (maintenant - confirmeLe < AGE_MAX_LOCAL &&
          confirmeLe >= instantDerniereEcriture() &&
          (locale.origine !== 'instantane' || confirmeLe >= instantDernierChangement()));
        if (frais) {
          if (maintenant - confirmeLe >= DELAI_REVALIDATION) revalider(config, chemin, parametres, locale.data, enAttente);
          return reponseLocale(config, locale.data, { entetes: locale.headers });
        }
        // Périmé: le serveur d'abord, s'il répond vite; sinon le local, et le
        // serveur continue en arrière-plan.
        const requete = interroger(config, chemin, parametres);
        requete.catch(() => {});
        const premier = await Promise.race([
          requete.then((reponse) => ({ reponse }), (erreur) => ({ erreur })),
          attendre(BUDGET_RESEAU),
        ]);
        if (premier?.reponse) return premier.reponse;
        if (premier?.erreur?.response || premier?.erreur?.code === 'ERR_CANCELED') throw premier.erreur;
        if (premier?.erreur) hooks.sonder?.();
        else {
          // Trop lent: l'écran a déjà ses chiffres locaux; la réponse tardive les remplace en silence.
          const tardive = requete
            .then(async (reponse) => {
              await enCours;
              if (!memeContenu(contenuJson(reponse), locale.data) && (await nombreEcrituresAEnvoyer()) === 0) {
                signalerChangement(chemin, cleDeLecture(chemin, parametres));
              }
            }, () => {})
            .finally(() => enVol.delete(tardive));
          enVol.add(tardive);
        }
        return reponseLocale(config, locale.data, { entetes: locale.headers });
      }

      try {
        return await interroger(config, chemin, parametres);
      } catch (erreur) {
        if (erreur?.response || erreur?.code === 'ERR_CANCELED') throw erreur;
        hooks.sonder?.();
      }
    }
    await enCours;
    const locale = await lireHorsLigne({ chemin, parametres });
    if (locale) return reponseLocale(config, locale.data, { entetes: locale.headers });
    throw erreurReseau(config);
  }

  const contexteDEcriture = ({ chemin, parametres, corps, match }) => ({
    chemin,
    parametres,
    corps,
    match,
    lire: lireEntite,
    ecrire: ecrireEntiteLocale,
    retirer: retirerEntiteLocale,
  });

  // Dépôt d'un document ou d'une photo: gardé sur l'appareil, envoyé au retour du réseau.
  async function ecrireDocument(config) {
    const formulaire = config.data;
    const fichier = formulaire.get('file');
    const champs = {};
    formulaire.forEach((valeur, nom) => {
      if (nom !== 'file') champs[nom] = String(valeur);
    });
    const cleRejeu = nouvelIdentifiant();
    if (hooks.enLigne()) {
      try {
        const tentative = { ...config, timeout: Math.max(config.timeout || 0, 60000) };
        tentative.headers = axios.AxiosHeaders.from(config.headers);
        tentative.headers.set('X-Replay-Key', cleRejeu);
        return await adaptateurDeBase(tentative);
      } catch (erreur) {
        if (erreur?.response || erreur?.code === 'ERR_CANCELED') throw erreur;
        hooks.sonder?.();
      }
    }
    if (!fichier || typeof fichier.size !== 'number') throw erreurReseau(config);
    const refusDocument = refusLocal('/documents');
    if (refusDocument) throw refusDeDroits(config, refusDocument);
    await enCours;
    const id = nouvelIdentifiant();
    const extension = (String(fichier.name || '').split('.').pop() || 'bin').toLowerCase();
    const url = `/api/v1/documents/files/${id}.${extension}`;
    const horodatage = new Date().toISOString();
    const document = {
      id,
      _id: id,
      file_name: fichier.name || 'document',
      file_type: fichier.type || 'application/octet-stream',
      doc_type: champs.doc_type || 'OTHER',
      url,
      entity_type: champs.entity_type || (champs.associated_trip_id ? 'trip' : null),
      entity_id: champs.entity_id || champs.associated_trip_id || null,
      associated_trip_id: champs.associated_trip_id || null,
      created_at: horodatage,
      updated_at: horodatage,
    };
    await ecrireEntiteLocale('/documents', id, document);
    await ecrireFichier(cleDeLecture(`/documents/files/${id}.${extension}`, {}), fichier);
    await hooks.mettreEnFile({
      methode: 'post',
      url: '/documents',
      params: {},
      corps: null,
      champs,
      fichier,
      nomFichier: fichier.name || 'document',
      entetes: {},
      cleRejeu,
      locaux: [{ kind: '/documents', id, cree: true }],
      fichierLocal: `/documents/files/${id}.${extension}`,
    });
    return reponseLocale(config, copie(document), { statut: 200 });
  }

  async function ecrire(config) {
    const { chemin, parametres } = decouperAdresse(config);
    if (
      concerne(chemin) &&
      !config.skipGateway &&
      String(config.method).toLowerCase() === 'post' &&
      chemin === '/documents' &&
      typeof FormData !== 'undefined' &&
      config.data instanceof FormData
    ) {
      return ecrireDocument(config);
    }
    const trouvee = concerne(chemin) && !config.skipGateway ? trouverRouteEcriture(config.method, chemin) : null;
    const corps = trouvee ? lireCorps(config) : undefined;
    if (!trouvee || corps === undefined) return adaptateurDeBase(config);

    const cleRejeu = nouvelIdentifiant();
    if (hooks.enLigne()) {
      try {
        const tentative = { ...config, timeout: Math.min(config.timeout || DELAI_PREMIERE_TENTATIVE, DELAI_PREMIERE_TENTATIVE) };
        tentative.headers = axios.AxiosHeaders.from(config.headers);
        tentative.headers.set('X-Replay-Key', cleRejeu);
        const reponse = await adaptateurDeBase(tentative);
        enfiler(() => confirmerEnLigne(trouvee, chemin, reponse));
        return reponse;
      } catch (erreur) {
        if (erreur?.response || erreur?.code === 'ERR_CANCELED') throw erreur;
        hooks.sonder?.();
      }
    }

    // Sans le serveur, les droits du compte sont ceux gardés sur l'appareil.
    const refus = refusLocal(chemin);
    if (refus) throw refusDeDroits(config, refus);
    await enCours;
    let resultat;
    try {
      resultat = await trouvee.route.appliquer(
        contexteDEcriture({ chemin, parametres, corps, match: trouvee.match }),
      );
    } catch (erreur) {
      // Une règle métier refuse l'écriture: même réponse que le serveur, rien n'est mis en file.
      if (erreur instanceof RefusLocal) throw refusDeDroits(config, erreur.message, erreur.statut, erreur.code);
      throw erreur;
    }
    if (!resultat) throw erreurReseau(config);

    const creations = (resultat.touches || []).filter((t) => t.cree);
    const corpsRejeu =
      trouvee.route.envoyerId && creations[0] && corps && typeof corps === 'object'
        ? { ...corps, id: creations[0].id }
        : corps;
    await hooks.mettreEnFile({
      methode: String(config.method || 'post').toLowerCase(),
      url: chemin,
      params: parametres,
      corps: corpsRejeu,
      entetes: lireEntetes(config, ENTETES_CONSERVES),
      cleRejeu,
      locaux: resultat.touches || [],
    });
    return reponseLocale(config, copie(resultat.reponse), { statut: resultat.statut || 200 });
  }

  // Écriture réussie en ligne: le modèle local reprend ce que le serveur a répondu.
  async function confirmerEnLigne(trouvee, chemin, reponse) {
    const corps = contenuJson(reponse);
    const entite = corps?.data && typeof corps.data === 'object' && !Array.isArray(corps.data) ? corps.data : corps;
    const touche = trouvee.route.noyau;
    if (!touche || !entite || typeof entite !== 'object' || Array.isArray(entite) || !entite.id) return;
    await ecrireEntitesServeur(touche, [entite]);
  }

  const traiter = async (config) => {
    if (hooks.desactivee?.()) return adaptateurDeBase(config);
    const methode = String(config.method || 'get').toLowerCase();
    if (methode === 'get' || methode === 'head') return lire(config);
    const reponse = await ecrire(config);
    // Une écriture est passée: les instantanés d'avant sont à revérifier.
    noterEcriture();
    return reponse;
  };

  // Les requêtes d'un écran passent avant le téléchargement de fond (`arrierePlan`).
  instance.defaults.adapter = async (config) => {
    if (config.arrierePlan) return traiter(config);
    debutPremierPlan();
    try {
      return await traiter(config);
    } finally {
      finPremierPlan();
    }
  };

  return {
    // Tout le travail de fond en cours: mémorisations et revalidations (tests, déconnexion).
    attendre: async () => {
      await enCours;
      await Promise.all([...revalidations.values(), ...enVol]);
      await enCours;
    },
  };
}

export { ALIAS_DE_LECTURE };
